// Korrekturbuchung – Belegart 'stock_adjustment': Bruch, Verderb, Schwund, Eigenverbrauch, Mehrbestand.
// Ein Beleg hat EINEN Grund (reasonCodeId im Kopf) und damit eine Richtung: Abgang, beim Grund „Mehrbestand“ Zugang;
// der Grund „Korrektur einer Fehlbuchung“ nennt die Richtung in customFields.direction. Bewertet wird zum gleitenden
// Durchschnitt. Liegt der Wert über der Grenze der erfassenden Person, wartet der Beleg auf Freigabe.
import { PERMISSION_COSTS_READ, defineContract } from '@shared/contract'
import { defineDocumentContracts, zDocumentView } from '@shared/document'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { APPROVAL_OUTCOMES, zDay, zNote, zReason, zUnitQty } from './common'

export const ADJUSTMENT_TYPE = 'stock_adjustment'

export const ADJUSTMENT_DIRECTIONS = ['out', 'in'] as const
export type AdjustmentDirection = (typeof ADJUSTMENT_DIRECTIONS)[number]

/** Gründe (reason_code.key), die einen Zugang buchen */
export const ADJUSTMENT_INBOUND_REASONS = ['found'] as const
/** Grund, bei dem die Richtung gewählt wird */
export const ADJUSTMENT_EITHER_REASON = 'correction'

const PERMISSIONS = {
  read: 'inventory.adjustment.read',
  write: 'inventory.adjustment.create',
  post: 'inventory.adjustment.create',
  reverse: 'inventory.adjustment.approve',
} as const

export const adjustment = defineDocumentContracts({
  module: 'inventory',
  entity: 'adjustment',
  documentType: ADJUSTMENT_TYPE,
  title: 'Korrekturbuchung',
  titlePlural: 'Korrekturbuchungen',
  permissions: PERMISSIONS,
  keywords: ['Korrektur', 'Bruch', 'Schwund', 'Verderb', 'Eigenverbrauch', 'ausbuchen', 'abschreiben', 'Mehrbestand'],
})

export const adjustmentGet = adjustment.get
export const adjustmentList = adjustment.list
export const adjustmentSaveDraft = adjustment.saveDraft
/** Buchen: bucht die Korrektur – oder stellt sie zur Freigabe, wenn ihr Wert über der Grenze liegt */
export const adjustmentPost = adjustment.post
export const adjustmentReverse = adjustment.reverse
export const adjustmentTransition = adjustment.transition
export const adjustmentPrint = adjustment.print
export const adjustmentDiscard = adjustment.discard

export const adjustmentBook = defineContract({
  name: 'inventory.adjustment.book',
  title: 'Korrekturbuchung erfassen und buchen',
  description: 'Bucht Bruch, Verderb, Schwund, Eigenverbrauch oder Mehrbestand in einem Schritt – über der Grenze geht die Korrektur zur Freigabe.',
  llm: {
    description:
      'Erfasst eine Korrekturbuchung in einem Schritt und bucht sie. `reasonCodeId` ist ein Grund der Art „adjustment“ ' +
      '(Bruch, Schwund, Verderb, Eigenverbrauch, Probe, Diebstahl, Mehrbestand, Korrektur) – er ' +
      'bestimmt die Richtung: alles außer „Mehrbestand (Fund)“ bucht AB; bei „Korrektur einer Fehlbuchung“ entscheidet ' +
      '`direction`. Je Zeile Artikel, Gebinde (`unitId`; ohne Angabe das Lagergebinde), Menge im ' +
      'Gebinde in Tausendstel (1 Karton = 1000, immer positiv) und ' +
      'optional den Warenposten (`lotId` oder `lotNo`, nur Betriebe mit Rückverfolgung; ohne Angabe wählt das System). Für einen ' +
      'gesperrten Posten diesen ausdrücklich nennen. Bewertet wird zum gleitenden Durchschnitt; liegt der Wert ' +
      'über der Grenze der Rolle, wird nichts gebucht und die Korrektur wartet auf Freigabe (`outcome: pending`). ' +
      'Nicht für Umlagerungen, Wareneingänge oder Inventurdifferenzen.',
    keywords: ['Bruch', 'Schwund', 'Verderb', 'Eigenverbrauch', 'ausbuchen', 'abschreiben', 'kaputt', 'Mehrbestand', 'Fund', 'Korrektur'],
  },
  input: z.strictObject({
    locationId: zId,
    reasonCodeId: zId,
    /** nur beim Grund „Korrektur einer Fehlbuchung“ */
    direction: z.enum(ADJUSTMENT_DIRECTIONS).optional(),
    /** Bemerkung – Pflicht, wenn der Grund sie verlangt */
    reason: z.string().trim().max(500).nullish(),
    documentDate: zDay.optional(),
    lines: z
      .array(
        z.strictObject({
          itemId: zId,
          unitId: zId.nullish(),
          qty: zUnitQty,
          lotId: zId.nullish(),
          lotNo: z.string().trim().max(80).nullish(),
          bestBefore: zDay.nullish(),
          note: zNote,
        }),
      )
      .min(1, { error: 'Bitte erfassen Sie mindestens eine Position.' })
      .max(200),
  }),
  output: z.object({ outcome: z.enum(APPROVAL_OUTCOMES), document: zDocumentView, message: z.string() }),
  permission: PERMISSIONS.write,
  risk: 'financial',
  scope: (input) => [input.locationId],
  fieldPermissions: { 'document.lines.*.costCents': PERMISSION_COSTS_READ },
  effects: { reversible: true },
  limits: { maxItems: 200, itemCount: (input) => input.lines.length },
})

export const adjustmentApprove = defineContract({
  name: 'inventory.adjustment.approve',
  title: 'Korrekturbuchung freigeben',
  description: 'Gibt eine Korrekturbuchung frei, die über der Grenze der erfassenden Person liegt, und bucht sie.',
  llm: {
    description:
      'Gibt eine Korrekturbuchung im Zustand „Zur Freigabe“ frei und bucht sie. Der Wert (gleitender Durchschnitt) ' +
      'muss innerhalb der Freigabegrenze der Rolle liegen, und niemand gibt die eigene Korrektur frei (Vier-Augen). ' +
      'Zum Zurückgeben inventory.adjustment.reject.',
    keywords: ['Korrektur freigeben', 'Freigabe', 'genehmigen', 'Abschreibung freigeben'],
  },
  input: z.strictObject({ id: zId }),
  output: zDocumentView,
  permission: 'inventory.adjustment.approve',
  risk: 'financial',
  scope: 'record',
  fieldPermissions: { 'lines.*.costCents': PERMISSION_COSTS_READ },
  effects: { reversible: true },
})

export const adjustmentReject = defineContract({
  name: 'inventory.adjustment.reject',
  title: 'Korrekturbuchung zurückgeben',
  description: 'Gibt eine Korrekturbuchung, die auf Freigabe wartet, mit Grund an die erfassende Person zurück.',
  llm: {
    description:
      'Lehnt die Freigabe einer Korrekturbuchung ab und gibt sie mit Grund zurück: Sie wird wieder bearbeitbar, ' +
      'gebucht wird nichts. Nur im Zustand „Zur Freigabe“.',
    keywords: ['Korrektur ablehnen', 'zurückgeben', 'Freigabe ablehnen'],
  },
  input: z.strictObject({ id: zId, reason: zReason }),
  output: zDocumentView,
  permission: 'inventory.adjustment.approve',
  risk: 'write',
  scope: 'record',
  fieldPermissions: { 'lines.*.costCents': PERMISSION_COSTS_READ },
})

/** Stand der Freigabe und Wert eines Belegs (Wareneingang oder Korrektur) – für die Karte „Freigabe“ neben dem Beleg. */
export const approvalState = defineContract({
  name: 'inventory.approval.state',
  title: 'Stand der Freigabe anzeigen',
  description: 'Zeigt, ob ein Wareneingang oder eine Korrekturbuchung auf Freigabe wartet, wer sie eingereicht hat und was der Nutzer tun darf.',
  llm: {
    description:
      'Liefert zu einem Wareneingang oder einer Korrekturbuchung den Stand der Freigabe: wartet sie, wer hat sie ' +
      'eingereicht, mit welchem Grund kam sie zurück, darf der Nutzer freigeben. `valueCents` (Wert in Cent) nur mit ' +
      'dem Recht „Einstandspreise sehen“.',
    keywords: ['Freigabe', 'wartet', 'eingereicht'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({
    id: zId,
    type: z.string(),
    status: z.string(),
    pending: z.boolean(),
    submittedBy: z.object({ id: zId, name: z.string() }).nullable(),
    submittedAt: z.date().nullable(),
    /** Grund der letzten Rückgabe */
    returnReason: z.string().nullable(),
    /** Richtung einer Korrekturbuchung */
    direction: z.enum(ADJUSTMENT_DIRECTIONS).nullable(),
    canApprove: z.boolean(),
    /** warum der Nutzer nicht freigeben darf (eigene Korrektur, Grenze) */
    approveHint: z.string().nullable(),
    valueCents: z.int().optional(),
  }),
  permission: 'inventory.stock.read',
  risk: 'read',
  scope: 'record',
  fieldPermissions: { valueCents: PERMISSION_COSTS_READ },
})
